import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import { queryCacheScope } from "./query-cache-scope";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import {
  projectJobRoleRecommendationsKey,
  projectJobRoleRecommendationsQuery,
} from "@/queries/project-resources";
import type {
  TJobRole,
  TCreateJobRole,
  TUpdateJobRole,
  TJobRoleMember,
  TJobRoleRecommendation,
  TJobRoleSummary,
} from "@/types/job-role";
import type { TWorkItemAssigneeDetail } from "@/types/issue";
import type { TMyMembership } from "@/services/workspace-job-role.service";

const EMPTY_MEMBERS: readonly TJobRoleMember[] = Object.freeze([]);

export class WorkspaceJobRolesStore {
  jobRoles: TJobRole[] = [];
  isLoading = false;
  activeJobRoleId: string | null = null;
  // Slug whose roles are loaded/in-flight, so consumers (e.g. the assignee
  // picker, mounted across many rows) can fetch once without re-requesting.
  private loadedSlug: string | null = null;

  // The work-item pickers' job-role choices, from the options list anyone who
  // can create or edit work may read (NG-2099). `jobRoles` is the settings list
  // and needs `job_role.read`, so a custom role without it would see no choices
  // there. Every write calls `invalidateJobRoleOptions`.
  jobRoleOptions: TJobRoleSummary[] = [];
  private optionsSlug: string | null = null;
  // Bumped by every load and every invalidation; only the newest load may
  // write, so a slower, superseded answer never overwrites a fresher one.
  private optionsRequest = 0;
  // The load in flight or last finished, so a second caller waits for it.
  private optionsLoad: Promise<void> = Promise.resolve();

  // Currently-assigned members per job role, so the members-cell trigger can
  // render its avatar stack in the CLOSED state (the popover-local list is only
  // loaded while open). Populated by `fetchJobRoleMembers` and refreshed by
  // `setJobRoleMembers` after a save, so the closed cell updates reactively
  // without a reopen — mirrors WorkspaceSkillsStore.usersBySkillId.
  membersByJobRoleId = observable.map<string, TJobRoleMember[]>();

  // Smart Assignment recommendations keyed by project id (Phase 6). The banner
  // mounts on every work view; keying by project keeps each project's state
  // separate as the user navigates.
  recommendationsByProject = observable.map<string, TJobRoleRecommendation[]>();
  recommendationsLoadingProject: string | null = null;
  smartAssigningRoleId: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** Fetch the workspace's job roles once per slug (idempotent). Safe to call
   *  from many components on mount; later calls for the same slug no-op. */
  async ensureJobRoles(slug: string): Promise<void> {
    if (this.loadedSlug === slug) return;
    this.loadedSlug = slug;
    try {
      await this.fetchJobRoles(slug);
    } catch (error) {
      this.loadedSlug = null;
      console.warn("Failed to ensure job roles", { slug, error });
    }
  }

  /** Fetch the pickers' job-role choices once per slug (idempotent, like
   *  `ensureJobRoles`). Safe to call from every assignee cell on mount. */
  ensureJobRoleOptions(slug: string): Promise<void> {
    if (this.optionsSlug === slug) return this.optionsLoad;
    this.optionsSlug = slug;
    this.optionsLoad = this.loadJobRoleOptions(slug, ++this.optionsRequest);
    return this.optionsLoad;
  }

  private async loadJobRoleOptions(slug: string, request: number): Promise<void> {
    try {
      const options = await this.rootStore.workspaceJobRoleService.listOptions(slug);
      runInAction(() => {
        if (request !== this.optionsRequest) return;
        this.jobRoleOptions = Array.isArray(options) ? options : [];
      });
    } catch (error) {
      runInAction(() => {
        if (request === this.optionsRequest) this.optionsSlug = null;
      });
      console.warn("Failed to load job role options", { slug, error });
    }
  }

  /** Drop the loaded choices so the next picker mount refetches rather than
   *  offering a renamed or deleted role. */
  private invalidateJobRoleOptions(): void {
    this.optionsSlug = null;
    this.optionsRequest += 1;
  }

  async fetchJobRoles(slug: string): Promise<TJobRole[]> {
    this.loadedSlug = slug;
    this.isLoading = true;
    try {
      const jobRoles = await this.rootStore.workspaceJobRoleService.list(slug);
      // Read the stored list back inside the action: post-`await` we are outside
      // any reactive context, so a bare `this.jobRoles` read here trips
      // `observableRequiresReaction` (NG-403).
      return runInAction(() => {
        // Never store a non-array: consumers (e.g. the create dialog's active
        // filter) call .filter() on this synchronously, so a nullish/unexpected
        // response must degrade to an empty list, not crash the render.
        this.jobRoles = Array.isArray(jobRoles) ? jobRoles : [];
        return this.jobRoles;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async createJobRole(
    slug: string,
    data: TCreateJobRole,
    source: AnalyticsSource = "page",
  ): Promise<TJobRole> {
    this.activeJobRoleId = "creating";
    try {
      const jobRole = await this.rootStore.workspaceJobRoleService.create(slug, data);
      runInAction(() => {
        this.jobRoles = [...this.jobRoles, jobRole];
        this.invalidateJobRoleOptions();
      });
      track("job_role_created", { job_role_id: jobRole.id, source });
      return jobRole;
    } finally {
      runInAction(() => {
        this.activeJobRoleId = null;
      });
    }
  }

  async updateJobRole(
    slug: string,
    id: string,
    data: TUpdateJobRole,
    source: AnalyticsSource = "page",
  ): Promise<TJobRole> {
    this.activeJobRoleId = id;
    // Read before the write: `fields` is what this save changed (NG-2063).
    const fields = changedFields(
      data,
      this.jobRoles.find((jobRole) => jobRole.id === id),
    );
    try {
      const jobRole = await this.rootStore.workspaceJobRoleService.update(slug, id, data);
      runInAction(() => {
        this.jobRoles = this.jobRoles.map((item) => (item.id === id ? jobRole : item));
        this.invalidateJobRoleOptions();
      });
      track("job_role_updated", { job_role_id: id, fields, source });
      return jobRole;
    } finally {
      runInAction(() => {
        this.activeJobRoleId = null;
      });
    }
  }

  async deleteJobRole(slug: string, id: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeJobRoleId = id;
    try {
      await this.rootStore.workspaceJobRoleService.remove(slug, id);
      track("job_role_deleted", { job_role_id: id, source });
      runInAction(() => {
        this.jobRoles = this.jobRoles.filter((jobRole) => jobRole.id !== id);
        this.invalidateJobRoleOptions();
      });
      // The backend unassigns the role from every work item on delete; mirror
      // that in the work-item cache so already-loaded board/list/detail views
      // drop the role chip without a refetch.
      this.rootStore.issue.clearJobRoleFromCache(id);
    } finally {
      runInAction(() => {
        this.activeJobRoleId = null;
      });
    }
  }

  /** Fetch the current user's own membership (for the self-service picker). */
  async fetchMyMembership(slug: string): Promise<TMyMembership> {
    return this.rootStore.workspaceJobRoleService.getMyMembership(slug);
  }

  /** Set (or clear) the current user's own job role via the "me" endpoint. */
  async setMyJobRole(
    slug: string,
    jobRoleId: string | null,
    source: AnalyticsSource = "page",
  ): Promise<TMyMembership> {
    const membership = await this.rootStore.workspaceJobRoleService.setMyJobRole(slug, jobRoleId);
    // The caller's own row in the member bank and the members table now
    // shows an old job role; the response is not that row, so mark them
    // stale rather than patch.
    this.rootStore.workspaceMembers.invalidateCachedMembers(slug).catch((error: unknown) => {
      console.warn("[workspace-job-roles] marking cached members stale failed", { slug }, error);
    });
    // A person setting their own job role.
    track("workspace_member_updated", { fields: ["job_role_id"], self: true, source });
    return membership;
  }

  /** Assigned members for a role, for rendering the closed members-cell avatar
   *  stack. Reads the observable cache seeded by `fetchJobRoleMembers` /
   *  `setJobRoleMembers`. */
  getJobRoleMembers(id: string): TJobRoleMember[] {
    return this.membersByJobRoleId.get(id) ?? (EMPTY_MEMBERS as TJobRoleMember[]);
  }

  /** Whether this role's assigned members were loaded (or mutated) this session
   *  — distinguishes "not fetched" from "has no holders", so the Members cell
   *  can prefer the fresher store copy over the list row's bounded
   *  `preview_user_ids`. */
  hasJobRoleMembers(id: string): boolean {
    return this.membersByJobRoleId.has(id);
  }

  /** Eligible members + assigned flags for the editor's member multiselect.
   *  Caches the currently-assigned subset so the closed cell can render avatars
   *  without the popover being open. */
  async fetchJobRoleMembers(slug: string, id: string): Promise<TJobRoleMember[]> {
    const members = await this.rootStore.workspaceJobRoleService.members(slug, id);
    runInAction(() => {
      this.membersByJobRoleId.set(
        id,
        members.filter((member) => member.assigned),
      );
    });
    return members;
  }

  /** Set the role's member set, then refresh the list so member counts update
   *  and re-fetch the assigned members so the closed cell's avatar stack updates
   *  reactively without a reopen. */
  async setJobRoleMembers(
    slug: string,
    id: string,
    memberIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    // The call sends the whole list: count what it adds and removes against the
    // members already loaded, before the refetch replaces them.
    const prior = (this.membersByJobRoleId.get(id) ?? []).map((member) => member.id);
    await this.rootStore.workspaceJobRoleService.setMembers(slug, id, memberIds);
    const { added, removed } = listChanges(prior, memberIds);
    if (added > 0) track("job_role_assigned", { job_role_id: id, count: added, source });
    if (removed > 0) track("job_role_unassigned", { job_role_id: id, count: removed, source });
    await Promise.all([this.fetchJobRoles(slug), this.fetchJobRoleMembers(slug, id)]);
  }

  getRecommendations(projectId: string): TJobRoleRecommendation[] {
    return this.recommendationsByProject.get(projectId) ?? [];
  }

  /** Refresh a project's recommendations only if they're already loaded — used
   *  by write paths elsewhere (e.g. assigning a job role to a work item in the
   *  list) to keep the Smart Assignment banner in sync, without forcing a fetch
   *  on projects whose banner was never shown. Fire-and-forget. */
  refreshRecommendationsIfLoaded(slug: string, projectId: string): void {
    if (!this.recommendationsByProject.has(projectId)) {
      // Not shown here, but it may be cached (R13): the next read must not be
      // answered with the recommendations from before the write.
      const scope = queryCacheScope(this.rootStore, slug, "WorkspaceJobRolesStore");
      if (!scope) return;
      peekQueryClient()
        ?.invalidateQueries({
          queryKey: projectJobRoleRecommendationsKey(scope, projectId),
          exact: true,
        })
        .catch((error: unknown) => {
          console.warn("[WorkspaceJobRolesStore] invalidating recommendations failed", {
            slug,
            projectId,
            error,
          });
        });
      return;
    }
    void this.loadRecommendations(slug, projectId, { force: true });
  }

  /**
   * The project's Smart Assignment recommendations, for the banner on mount.
   * With the `query-cache` flag on, the read goes through the cached
   * recommendations (R13, `app/queries/project-resources.ts`), so re-entering
   * the project's work items within the freshness window makes no request.
   */
  fetchRecommendations(slug: string, projectId: string): Promise<void> {
    return this.loadRecommendations(slug, projectId, { force: false });
  }

  /** Read the recommendations; `force` skips the cache, after a write. */
  private async loadRecommendations(
    slug: string,
    projectId: string,
    { force }: { force: boolean },
  ): Promise<void> {
    const scope = queryCacheScope(this.rootStore, slug, "WorkspaceJobRolesStore");
    this.recommendationsLoadingProject = projectId;
    try {
      const service = this.rootStore.workspaceJobRoleService;
      const query = scope && projectJobRoleRecommendationsQuery(service, scope, projectId);
      const recommendations = query
        ? await getQueryClient().fetchQuery(force ? { ...query, staleTime: 0 } : query)
        : await service.recommendations(slug, projectId);
      runInAction(() => {
        this.recommendationsByProject.set(projectId, recommendations);
      });
    } catch (error) {
      console.warn("Failed to load Smart Assignment recommendations", { slug, projectId, error });
    } finally {
      runInAction(() => {
        this.recommendationsLoadingProject = null;
      });
    }
  }

  /** Bulk-assign a job role's project work to one user, then refresh the
   *  recommendations (the role drops off once its work is staffed). The caller
   *  surfaces the toast; over-allocation is confirmed before this runs.
   *
   *  `assigneeDetail` (the picked candidate's user summary) is threaded through
   *  to the work-item cache so the assignee cell can render the name without
   *  waiting for a refetch — see IssueStore.applySmartAssignToCache. */
  async smartAssign(
    slug: string,
    projectId: string,
    jobRoleId: string,
    assigneeId: string,
    assigneeDetail?: TWorkItemAssigneeDetail,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.smartAssigningRoleId = jobRoleId;
    try {
      const result = await this.rootStore.workspaceJobRoleService.smartAssign(
        slug,
        projectId,
        jobRoleId,
        assigneeId,
      );
      // Reflect the bulk assignment in the work-item cache so the board/list/
      // table stop showing the (now-cleared) job role and show the assignee —
      // the views render from the cache, not from the recommendations refetch.
      this.rootStore.issue.applySmartAssignToCache(
        result.work_item_ids,
        result.assignee_id,
        assigneeDetail,
      );
      // A task write: the tasks lane's bulk assignment shape, with the job role.
      track("task_assigned", {
        project_id: projectId,
        member_id: assigneeId,
        job_role_id: jobRoleId,
        count: result.work_item_ids.length,
        source,
      });
      await this.loadRecommendations(slug, projectId, { force: true });
    } finally {
      runInAction(() => {
        this.smartAssigningRoleId = null;
      });
    }
  }

  /**
   * Optimistic status flip for the list toggle: mutate the row immediately,
   * persist in the background, and revert on failure. The caller surfaces the
   * toast; deactivation is gated behind a confirm modal before this runs.
   */
  async setStatus(
    slug: string,
    id: string,
    status: TJobRole["status"],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const previous = this.jobRoles.find((jobRole) => jobRole.id === id);
    if (!previous) return;
    const snapshot = previous.status;
    runInAction(() => {
      this.jobRoles = this.jobRoles.map((item) => (item.id === id ? { ...item, status } : item));
    });
    try {
      const jobRole = await this.rootStore.workspaceJobRoleService.update(slug, id, { status });
      runInAction(() => {
        this.jobRoles = this.jobRoles.map((item) => (item.id === id ? jobRole : item));
        this.invalidateJobRoleOptions();
      });
      if (status !== snapshot) {
        track(status === "inactive" ? "job_role_deactivated" : "job_role_activated", {
          job_role_id: id,
          source,
        });
      }
      // Deactivating (active -> inactive) unassigns the role from all work items
      // on the backend, same as delete — clear the work-item cache to match.
      if (snapshot === "active" && status === "inactive") {
        this.rootStore.issue.clearJobRoleFromCache(id);
      }
    } catch (error) {
      runInAction(() => {
        this.jobRoles = this.jobRoles.map((item) =>
          item.id === id ? { ...item, status: snapshot } : item,
        );
      });
      throw error;
    }
  }
}
